// R60 — SHE OPENS A CONVERSATION SHE PAID FOR TURN BY TURN, AND IT OPENS EMPTY.
//
// THE HUNT is the pointer's, and this is the last of the fabricated-collection census. The two
// opening reads of Ask Lala were written as **FOUR forged empty lists on TWO lines**:
//
//     fetch("/api/lala/messages").then((r) => (r.ok ? r.json() : { messages: [] })).catch(() => ({ messages: [] })),
//     fetch("/api/lala/attachments").then((r) => (r.ok ? r.json() : { attachments: [] })).catch(() => ({ attachments: [] })),
//
// — a fallback on the status branch AND a second one in a trailing `.catch`, so the lie is written
// twice per route. `Array.isArray(hist.messages)` then says yes to the forgery (R55's finding word
// for word: the guard that looks like it is protecting her is exactly what admits the defect), and
// the drawer paints an empty conversation over a thread she has been having for months.
//
// **AND THE CENSUS UNDERSTATED IT AGAIN, FOR THE REASON IT UNDERSTATED R58: IT LISTED BY LITERAL.**
// The Ask Lala PAGE reads the same two routes and carries no `messages: []` anywhere, so it was
// never on the list — its `openConversation` does `setMessages([])`, then `data.messages || []`,
// then `catch { /* leave empty */ }`. **Every one of those three paths ends at an empty thread**,
// and this is the worse of the two surfaces: the drawer opens on nothing anyway, but on the page
// she TAPPED a named conversation in her sidebar and is shown a blank one. Nothing red, no sentence,
// no way to tell a lost request from a conversation that somehow emptied itself.
//
// The two routes answer differently and the difference is kept:
//   * `/api/lala/messages` — 200 `{ messages }`; 200 `{ messages: [] }` SIGNED OUT (a real answer:
//     no session, no thread, and it is DECLARED rather than fixed, R59's precedent on the bookmark
//     GET); 200 `{ ok: false }` when `ensureUser` comes back empty; 500 on a throw. The last two
//     used to be `{ messages: [] }` on the SERVER as well — five of the last seven rounds have found
//     the same lie at both ends.
//   * `/api/lala/attachments` — 401; 200 `{ ok: false }` on an empty `ensureUser`; 200
//     `{ ok: true, attachments: [] }` for a thread that is not hers, which is a REAL answer and stays
//     one; 500 on a throw.
//
// **THE TWO FAILURES ARE NOT THE SAME SIZE AND THEY ARE NOT COLLAPSED.** No messages is no
// conversation. No attachments is her conversation with the files missing from it, which is a real
// loss ("here, look at this" with nothing under it) and a much smaller one. Two rows, one note
// function, and the bigger loss wins when both fail.
//
// **`attachToMessages` IS THE ROUND'S DERIVATION (R31/R41).** The map that puts each file back
// beside the message it was sent with was typed byte-for-byte in the drawer and again on the page,
// 4,900 lines apart — R47's count-the-copies currency, and the copies had already drifted (the page
// mapped `data.messages || []` while the drawer mapped `hist.messages`). One law, two readers.
//
// This module imports NOTHING (the entry requirement for `scripts/`-loadable law).

export const lalaMessagesPath = (conversationId?: number | string | null) =>
  conversationId === undefined || conversationId === null || conversationId === ""
    ? "/api/lala/messages"
    : `/api/lala/messages?conversationId=${encodeURIComponent(String(conversationId))}`;

export const lalaAttachmentsPath = (conversationId?: number | string | null) =>
  conversationId === undefined || conversationId === null || conversationId === ""
    ? "/api/lala/attachments"
    : `/api/lala/attachments?conversationId=${encodeURIComponent(String(conversationId))}`;

// The two shapes this module needs to do its work, and NOTHING else about a message or a file. The
// drawer and the page carry different message types (the page's rows have an `index`, the drawer's
// do not) and both flow through here, so the parameter is structural rather than a third roster of
// what a Lala message is.
export type ThreadMessage = { id?: string };
export type ThreadAttachment = { messageId?: string | null };

type Json = Record<string, unknown> | null;

// **A FAILED READ IS NOT AN EMPTY THREAD** (R18/R53/R55/R57/R58/R59). It answers null, so a surface
// keeps whatever it was already showing and SAYS so, rather than painting "you have said nothing to
// her" out of a request that lost its way. The server's own empty list — signed out, or an account
// that really has no turns — is a genuine `{ messages: [] }` and stays an answer: the two are told
// apart by the SHAPE, not by the status code.
//
// `doFetch` is a PARAMETER (R46's locked-door shape) so a gate drives every answer the route can
// give without a browser, a session or a database.
// FUNNEL-4 — the READ carries the allotment beside the thread, in ONE round trip. Her counter has to
// be on screen the moment she lands (clause 3), and a second fetch for a number the first answer
// already knows is a second answer to *how many turns has she got* as well as a second request
// (R28/R29). It REPLACED `listLalaMessages` at its declaration rather than being added beside it:
// once both surfaces read this, that function had no product caller left and only its own fixtures
// drove it, which is R165's shape exactly.
export async function readLalaThread<T extends ThreadMessage>(
  conversationId?: number | string | null,
  doFetch: typeof fetch = fetch,
): Promise<{ messages: T[]; freeTurns: unknown; earlier: boolean } | null> {
  try {
    const res = await doFetch(lalaMessagesPath(conversationId));
    if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return null; }
    const data = (await res.json().catch(() => null)) as Json;
    const rows = data ? data.messages : null;
    if (!Array.isArray(rows)) return null;
    return { messages: rows as T[], freeTurns: data ? data.freeTurns : undefined, earlier: Boolean(data && data.earlier === true) };
  } catch {
    return null;
  }
}

// >>> **IT #322 — THE MESSAGES OLDER THAN THE ONES ON HER SCREEN.** <<< The first read hands over
// the newest 200, and a thread longer than that used to end there for good: Na'ilah's four weeks of
// working instructions were simply gone. This reads the page before her oldest message. **A failed
// read is null, never an empty page**, so the screen keeps what it has and keeps its button.
export const lalaEarlierPath = (conversationId: number | string | null | undefined, beforeId: string) =>
  `${lalaMessagesPath(conversationId)}${conversationId === undefined || conversationId === null || conversationId === "" ? "?" : "&"}before=${encodeURIComponent(beforeId)}`;

export async function readEarlierLalaMessages<T extends ThreadMessage>(
  conversationId: number | string | null | undefined,
  beforeId: string | undefined,
  doFetch: typeof fetch = fetch,
): Promise<{ messages: T[]; earlier: boolean } | null> {
  if (!beforeId) return null;
  try {
    const res = await doFetch(lalaEarlierPath(conversationId, beforeId));
    if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return null; }
    const data = (await res.json().catch(() => null)) as Json;
    const rows = data ? data.messages : null;
    if (!data || data.ok !== true || !Array.isArray(rows)) return null;
    return { messages: rows as T[], earlier: data.earlier === true };
  } catch {
    return null;
  }
}

// The earlier page goes ABOVE what she has, and a message already on screen is never drawn twice.
export function prependEarlier<M extends ThreadMessage>(current: M[], older: M[]): M[] {
  const have = new Set(current.map((m) => m.id).filter(Boolean));
  return [...older.filter((m) => !m.id || !have.has(m.id)), ...current];
}

// The same law on the smaller half. A thread that is not hers answers a real empty list and that is
// NOT a failure — the ownership check upstream already decided it, and turning it into a red line
// would tell her something about a conversation she does not own.
export async function listLalaAttachments<A extends ThreadAttachment>(
  conversationId?: number | string | null,
  doFetch: typeof fetch = fetch,
): Promise<A[] | null> {
  try {
    const res = await doFetch(lalaAttachmentsPath(conversationId));
    if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return null; }
    const data = (await res.json().catch(() => null)) as Json;
    const rows = data ? data.attachments : null;
    return Array.isArray(rows) ? (rows as A[]) : null;
  } catch {
    return null;
  }
}

// U4's law, ONCE. Each file goes back beside the message it was sent with, in the order it arrived;
// a file carrying no `messageId` belongs to no turn and is dropped rather than piled onto the first
// one. **A null attachments list is not an empty one** — the messages still render, without their
// files, and the note below is what says so.
export function attachToMessages<M extends ThreadMessage, A extends ThreadAttachment>(
  messages: M[],
  attachments: A[] | null,
): Array<M & { attachments: A[] }> {
  const byMessage = new Map<string, A[]>();
  for (const a of attachments || []) {
    if (!a.messageId) continue;
    byMessage.set(a.messageId, [...(byMessage.get(a.messageId) || []), a]);
  }
  return messages.map((m) => ({ ...m, attachments: m.id ? byMessage.get(m.id) || [] : [] }));
}

export type ThreadCopy = { thread: string; files: string };

// The sentence, derived from the two answers rather than typed per surface. **A SUCCESS SAYS
// NOTHING** (R51/R53/R54/R55/R57/R58 on the same question) — the conversation being there IS the
// answer. **THE BIGGER LOSS WINS**: if the thread did not arrive there is no point telling her its
// files did not either, and two red lines over one blank panel is noise.
export function lalaThreadNote(
  messages: unknown[] | null,
  attachments: unknown[] | null,
  copy: ThreadCopy,
): string {
  if (messages === null) return copy.thread;
  if (attachments === null) return copy.files;
  return "";
}

// ---------------------------------------------------------------------------------------------
// R62 — THE THREAD SHE IS TALKING IN, WHEN THE THREAD WAS NEVER MADE.
//
// THE HUNT is the one R60 and R61 sharpened and this file's own round left in the RUNBOOK: **`|| []`
// ON A PAYLOAD FIELD — the same forgery as `r.ok ? r.json() : { x: [] }` without the object literal
// around it, which is why two censuses over literals walked straight past it.** 87 sites carry that
// shape; almost all are defensive renderers over data already in hand. `/api/lala/conversations` is
// where it is a read, and it is the LAST unheld route in this room — R60 took the thread's contents
// and this is the list the thread is named on.
//
// **THE FINDING IS THE CREATE, AND IT IS THE WORST OF THE THREE BECAUSE IT COSTS A PAID TURN.**
// `doSend` opens a conversation before it sends:
//
//     const res = await fetch("/api/lala/conversations", { method: "POST", … });
//     const data = await res.json().catch(() => ({}));
//     convId = data.conversation?.id ?? null;
//     if (convId !== null) setActiveId(convId);
//
// — and then sends anyway with `conversationId: convId`. **So a refused create is not an error, it
// is a null**, her question goes out, Lala answers it, the owner is charged for the turn, and the
// whole exchange is filed under NO NAMED THREAD. It never appears in her sidebar. `loadConversations`
// runs right after and finds nothing new, which looks exactly like a member who has saved nothing.
// R17's silence, on the most expensive surface in the house.
//
// The LIST is the same route one method over: `(data.conversations || [])` then `setConversations`,
// so a 200 refusal or an unparseable body **blanks her whole sidebar** — every named conversation
// she has, gone from the screen, while `!res.ok` and the `catch` (correctly) keep it. Three paths,
// two behaviours, and the wrong one is the path the server actually takes when it refuses.
//
// The route answers: **401**; **HTTP 200 `{ conversations: [] }`** on an empty `ensureUser` (the
// SEVENTH route found answering a refusal in the shape of an empty collection — closed this round);
// **200 `{ ok: false }`** from `createConversation`/`deleteConversation` returning nothing; **405**;
// **500** "Could not load your conversations."
//
// `groupConversations` moves here with them. It is the sidebar's whole shape — group by collection,
// unfiled LAST — and it was a nine-line loop inside a component holding two dozen hooks, so nothing
// could run it. It is the round's line payment in R37/R42's currency: move the law, not the widget.
//
// `doFetch` is a PARAMETER (R46) so a gate drives every answer without a browser or a database.
// ---------------------------------------------------------------------------------------------

export const lalaConversationsPath = "/api/lala/conversations";
export const lalaConversationPath = (id: number | string) =>
  `${lalaConversationsPath}/${encodeURIComponent(String(id))}`;

export type ConversationAnswer =
  | { ok: true; id: number | null }
  | { ok: false; kind: "sign-in" }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

type ConversationJson = { ok?: boolean; message?: string; conversation?: { id?: number } };

// THE LIST. **A FAILED READ IS NOT AN EMPTY SIDEBAR** (R18/R53/R55/R57/R58/R59/R60): null, so the
// sidebar keeps every conversation it was already showing. The server's own empty list — an account
// that really has saved nothing — is a genuine `{ conversations: [] }` and stays an answer, told
// apart by the SHAPE rather than by the status code.
export async function listLalaConversations<T>(doFetch: typeof fetch = fetch): Promise<T[] | null> {
  try {
    const res = await doFetch(lalaConversationsPath);
    if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return null; }
    const data = (await res.json().catch(() => null)) as { conversations?: unknown } | null;
    return Array.isArray(data?.conversations) ? (data.conversations as T[]) : null;
  } catch {
    return null;
  }
}

// THE CREATE. **THE NEW ROW IS THE PROOF** (R18's seal-status lesson, R53/R61's `data` guard): a 200
// with no `conversation.id` on it is not a thread, and answering `{ ok: true, id: null }` there would
// hand the caller back the exact null this round exists to stop being silent.
export async function createLalaConversation(
  title: string,
  collection: string,
  doFetch: typeof fetch = fetch,
): Promise<ConversationAnswer> {
  try {
    const res = await doFetch(lalaConversationsPath, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title, collection }),
    });
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = (await res.json().catch(() => ({}))) as ConversationJson;
    const id = data?.conversation?.id;
    if (res.ok && data?.ok !== false && typeof id === "number") return { ok: true, id };
    return { ok: false, kind: "refused", message: typeof data?.message === "string" ? data.message : "" };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// THE DELETE. R35 gave this one a CONFIRM (a saved thread is every turn she paid for), and the
// payload read was already honest — it is here so this route has ONE answer-reader rather than two
// thirds of one (R53's ruling on the same shape).
export async function deleteLalaConversation(id: number, doFetch: typeof fetch = fetch): Promise<ConversationAnswer> {
  try {
    const res = await doFetch(lalaConversationPath(id), { method: "DELETE" });
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = (await res.json().catch(() => ({}))) as ConversationJson;
    if (res.ok && data?.ok !== false) return { ok: true, id };
    return { ok: false, kind: "refused", message: typeof data?.message === "string" ? data.message : "" };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// The sentence. **A SUCCESS SAYS NOTHING** — the thread appearing in her sidebar IS the answer.
export function lalaConversationNote(answer: ConversationAnswer, copy: { signIn: string; failed: string }): string {
  if (answer.ok) return "";
  if (answer.kind === "sign-in") return copy.signIn;
  if (answer.kind === "refused" && answer.message) return answer.message;
  return copy.failed;
}

// THE SIDEBAR'S SHAPE, and it is a law rather than a loop: **collections in name order, UNFILED
// LAST**, and every conversation in exactly one group in the order the server sent them (the list is
// served newest-first, so a re-sort inside a group would silently reorder her threads).
export function groupConversations<T extends { collection?: string }>(
  conversations: T[],
): Array<{ name: string; items: T[] }> {
  const groups: Array<{ name: string; items: T[] }> = [];
  for (const c of conversations || []) {
    const key = c.collection || "";
    let g = groups.find((x) => x.name === key);
    if (!g) { g = { name: key, items: [] }; groups.push(g); }
    g.items.push(c);
  }
  groups.sort((a, b) => (a.name === "" ? 1 : b.name === "" ? -1 : a.name.localeCompare(b.name)));
  return groups;
}

// R91 — **THE FOUR SENTENCES THIS TRANSPORT'S OWN NOTES ARE COMPOSED FROM, MOVED TO THE LAW THAT
// COMPOSES THEM.** They were a `const` in App.tsx handed back in as an argument at three call sites,
// which is the shape R24 named: the law is here and the words it reads were over there, held by
// nothing but two fixtures matching the declaration as TEXT. `lalaThreadNote` and
// `lalaConversationNote` take a copy object as a PARAMETER and still do — a gate drives them with
// probe sentences — so this is the DEFAULT they are called with in the house, not a second answer key.
//
// **DELIBERATELY ENGLISH AND NOT A CATALOG ROW**, and that is 14-B6's own lesson rather than an
// omission: the Ask Lala surfaces' remaining sentences are English (it is a named open item), and a
// lone translated line over an untranslated screen IS the defect.
export const LALA_THREAD_COPY = {
  thread: "Your earlier conversation could not be loaded just now. Refresh and it will come back.",
  files: "The files in this conversation could not be loaded just now.",
  signIn: "Sign in to keep this conversation.",
  notSaved: "This conversation could not be saved under its own name just now. Lala still answers; look for it in the drawer.",
  // IT #322: the button above a thread longer than one read, and what it says when the read fails.
  earlier: "Show earlier messages",
  earlierLoading: "Bringing them back...",
  earlierFailed: "Your earlier messages could not be loaded just now. Tap to try again.",
};
